import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Usb, Radio, Play, Square, ChevronDown, ChevronRight, FileUp } from "lucide-react";

/**
 * RelacartConnectionPanel - Panel for Relacart DISCOVER HID connection
 */
export function RelacartConnectionPanel({
  hidConnected,
  hidDevice,
  hidDeviceInfo,
  hidScanning,
  isHIDSupported,
  connectHID,
  disconnectHID,
  startHIDScan,
  stopHIDScan,
  globalRawDataLog,
  onImportClick,
}) {
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  return (
    <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${
          hidConnected ? "bg-[#34C759]/20" : "bg-[#71717A]/20"
        }`}>
          <Radio className={`w-4 h-4 ${hidConnected ? "text-[#34C759]" : "text-[#71717A]"}`} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-[#F4F4F5]">Relacart DISCOVER</h3>
          <p className="text-xs text-[#71717A]">
            {hidConnected ? "Conectado" : "WebHID USB"}
          </p>
        </div>
        {hidConnected && (
          <span className="ml-auto flex items-center gap-1 text-xs text-[#34C759]">
            <span className="w-2 h-2 bg-[#34C759] rounded-full animate-pulse" />
            EN VIVO
          </span>
        )}
      </div>
      
      {/* Connection buttons */}
      {!hidConnected ? (
        <div className="flex flex-col gap-2 mb-3">
          <Button
            onClick={() => connectHID(true)}
            disabled={!isHIDSupported}
            className="w-full bg-[#34C759] hover:bg-[#34C759]/90 text-white"
            data-testid="connect-relacart-hid"
          >
            <Usb className="w-4 h-4 mr-2" />Conectar Relacart
          </Button>
          <Button
            onClick={() => connectHID(false)}
            disabled={!isHIDSupported}
            variant="outline"
            className="w-full border-white/20 text-white hover:bg-white/5 text-xs"
          >
            <Radio className="w-3 h-3 mr-2" />
            Buscar otros dispositivos HID
          </Button>
        </div>
      ) : (
        <div className="space-y-2 mb-3">
          {/* Device info */}
          <div className="p-2 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
            <div className="flex items-center gap-2 mb-1">
              <Usb className="w-4 h-4 text-[#34C759]" />
              <span className="text-white font-medium text-sm">{hidDevice?.name}</span>
            </div>
            <div className="grid grid-cols-2 gap-1 text-xs">
              <div><span className="text-[#71717A]">VID:</span> <span className="text-white font-mono">0x{hidDevice?.vendorId}</span></div>
              <div><span className="text-[#71717A]">PID:</span> <span className="text-white font-mono">0x{hidDevice?.productId}</span></div>
              {hidDeviceInfo?.serial && (
                <div className="col-span-2"><span className="text-[#71717A]">Serial:</span> <span className="text-[#34C759] font-mono text-[10px]">{hidDeviceInfo.serial}</span></div>
              )}
            </div>
          </div>
          
          {/* Scan controls */}
          <div className="flex gap-2">
            {!hidScanning ? (
              <Button
                onClick={startHIDScan}
                size="sm"
                className="flex-1 bg-[#007AFF] hover:bg-[#007AFF]/90 text-white text-xs h-8"
              >
                <Play className="w-3 h-3 mr-1" />Iniciar Escaneo
              </Button>
            ) : (
              <Button
                onClick={stopHIDScan}
                size="sm"
                variant="destructive"
                className="flex-1 text-xs h-8"
              >
                <Square className="w-3 h-3 mr-1" />Detener
              </Button>
            )}
            <Button
              onClick={disconnectHID}
              size="sm"
              variant="ghost"
              className="text-[#FF3B30] hover:bg-[#FF3B30]/10"
            >
              Desconectar
            </Button>
          </div>
        </div>
      )}
      
      {/* Debug panel toggle */}
      <div className="border-t border-white/[0.05] pt-3">
        <Button
          onClick={() => setShowDebugPanel(!showDebugPanel)}
          variant="ghost"
          size="sm"
          className="w-full justify-between text-xs text-[#71717A] h-7"
        >
          <span>Debug HID ({globalRawDataLog?.length || 0} mensajes)</span>
          {showDebugPanel ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </Button>
        
        {showDebugPanel && (
          <div className="mt-2 bg-black/50 rounded p-2 max-h-32 overflow-y-auto font-mono text-[10px]">
            {!globalRawDataLog?.length ? (
              <p className="text-[#71717A]">Sin datos...</p>
            ) : (
              globalRawDataLog.slice(-10).map((entry) => (
                <div key={`${entry.time}-${entry.reportId}`} className="text-[#A1A1AA] mb-1 border-b border-white/5 pb-1">
                  <div className="text-[#71717A]">[{entry.time}] #{entry.reportId}</div>
                  <div className="text-[#34C759]">{entry.ascii}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      
      {/* Import CSV fallback */}
      <div className="mt-3 pt-3 border-t border-white/[0.05]">
        <Button
          onClick={onImportClick}
          variant="outline"
          size="sm"
          className="w-full border-white/10 text-[#71717A] hover:text-white text-xs"
        >
          <FileUp className="w-3 h-3 mr-2" />
          Importar CSV (alternativa)
        </Button>
      </div>
    </div>
  );
}

export default RelacartConnectionPanel;
